import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Button, IconButton, Tooltip, Alert,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress
} from '@mui/material';
import {
  CheckCircle, Cancel, Visibility, Computer, Person, Phone, Email,
  Business, Refresh, AccessTime, Language
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';

interface SetupRequest {
  id: number;
  clinicName: string;
  contactName: string;
  phone: string;
  email: string;
  ipAddress: string | null;
  computerName: string | null;
  agentToken: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  serverId: number | null;
  clientId: number | null;
  createdAt: string;
}

const SetupRequests: React.FC = () => {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<SetupRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [detailDialog, setDetailDialog] = useState<SetupRequest | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number; action: 'approve' | 'reject' }>({ open: false, id: 0, action: 'approve' });
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/setup-requests');
      setRequests(res.data);
    } catch {
      setSnackbar({ open: true, message: t('admin.setupRequests.fetchFailed'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async () => {
    setActionLoading(true);
    try {
      await api.post(`/admin/setup-requests/${confirmDialog.id}/${confirmDialog.action}`);
      setSnackbar({
        open: true,
        message: confirmDialog.action === 'approve' ? t('admin.setupRequests.approved') : t('admin.setupRequests.rejected'),
        severity: 'success'
      });
      setConfirmDialog({ open: false, id: 0, action: 'approve' });
      fetchRequests();
    } catch {
      setSnackbar({ open: true, message: t('admin.setupRequests.actionFailed'), severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Chip label={t('admin.setupRequests.pending')} color="warning" size="small" />;
      case 'APPROVED':
        return <Chip label={t('admin.setupRequests.approvedStatus')} color="success" size="small" />;
      case 'REJECTED':
        return <Chip label={t('admin.setupRequests.rejectedStatus')} color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const pendingCount = requests.filter(r => r.status === 'PENDING').length;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {t('admin.setupRequests.title')}
            {pendingCount > 0 && (
              <Chip label={pendingCount} color="warning" size="small" sx={{ ml: 1, fontWeight: 700 }} />
            )}
          </Typography>
          <Typography color="text.secondary">{t('admin.setupRequests.subtitle')}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchRequests}>
          {t('common.refresh')}
        </Button>
      </Box>

      {/* Setup Link Card */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: '#e8eaf6', borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Language color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('admin.setupRequests.setupLink')}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: 'monospace', bgcolor: '#fff', p: 2, borderRadius: 2,
            border: '1px solid #c5cae9', direction: 'ltr', textAlign: 'left',
            cursor: 'pointer', '&:hover': { bgcolor: '#f5f5f5' }
          }}
          onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/setup`);
            setSnackbar({ open: true, message: t('admin.setupRequests.linkCopied'), severity: 'success' });
          }}
        >
          {window.location.origin}/setup
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {t('admin.setupRequests.linkHint')}
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}><CircularProgress /></Box>
      ) : requests.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Computer sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">{t('admin.setupRequests.noRequests')}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>{t('admin.setupRequests.clinic')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('admin.setupRequests.contact')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('common.phone')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('admin.setupRequests.ip')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('common.status')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('common.createdAt')}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((req) => (
                <TableRow key={req.id} sx={{ bgcolor: req.status === 'PENDING' ? '#fff8e1' : undefined }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business fontSize="small" color="primary" />
                      <Typography fontWeight={600}>{req.clinicName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{req.contactName}</TableCell>
                  <TableCell sx={{ direction: 'ltr' }}>{req.phone}</TableCell>
                  <TableCell sx={{ direction: 'ltr', fontFamily: 'monospace' }}>
                    {req.ipAddress || '-'}
                    {req.computerName && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {req.computerName}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{getStatusChip(req.status)}</TableCell>
                  <TableCell>{new Date(req.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={t('admin.setupRequests.viewDetails')}>
                        <IconButton size="small" onClick={() => setDetailDialog(req)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {req.status === 'PENDING' && (
                        <>
                          <Tooltip title={t('admin.setupRequests.approve')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => setConfirmDialog({ open: true, id: req.id, action: 'approve' })}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('admin.setupRequests.reject')}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setConfirmDialog({ open: true, id: req.id, action: 'reject' })}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('admin.setupRequests.requestDetails')}</DialogTitle>
        {detailDialog && (
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Business color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('admin.setupRequests.clinic')}</Typography>
                  <Typography fontWeight={600}>{detailDialog.clinicName}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('admin.setupRequests.contact')}</Typography>
                  <Typography fontWeight={600}>{detailDialog.contactName}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('common.phone')}</Typography>
                  <Typography fontWeight={600} sx={{ direction: 'ltr' }}>{detailDialog.phone}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('common.email')}</Typography>
                  <Typography fontWeight={600} sx={{ direction: 'ltr' }}>{detailDialog.email}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Computer color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('admin.setupRequests.ip')}</Typography>
                  <Typography fontWeight={600} sx={{ direction: 'ltr', fontFamily: 'monospace' }}>
                    {detailDialog.ipAddress || t('admin.setupRequests.noIpYet')}
                    {detailDialog.computerName && ` (${detailDialog.computerName})`}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTime color="primary" />
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('common.createdAt')}</Typography>
                  <Typography fontWeight={600}>{new Date(detailDialog.createdAt).toLocaleString()}</Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                <Box sx={{ mt: 0.5 }}>{getStatusChip(detailDialog.status)}</Box>
              </Box>
            </Box>
          </DialogContent>
        )}
        <DialogActions>
          {detailDialog?.status === 'PENDING' && (
            <>
              <Button
                color="success"
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={() => {
                  setDetailDialog(null);
                  setConfirmDialog({ open: true, id: detailDialog.id, action: 'approve' });
                }}
              >
                {t('admin.setupRequests.approve')}
              </Button>
              <Button
                color="error"
                variant="outlined"
                startIcon={<Cancel />}
                onClick={() => {
                  setDetailDialog(null);
                  setConfirmDialog({ open: true, id: detailDialog.id, action: 'reject' });
                }}
              >
                {t('admin.setupRequests.reject')}
              </Button>
            </>
          )}
          <Button onClick={() => setDetailDialog(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, id: 0, action: 'approve' })}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirmDialog.action === 'approve' ? t('admin.setupRequests.confirmApprove') : t('admin.setupRequests.confirmReject')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.action === 'approve' ? t('admin.setupRequests.approveDesc') : t('admin.setupRequests.rejectDesc')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, id: 0, action: 'approve' })}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.action === 'approve' ? 'success' : 'error'}
            onClick={handleAction}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : undefined}
          >
            {confirmDialog.action === 'approve' ? t('admin.setupRequests.approve') : t('admin.setupRequests.reject')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SetupRequests;
